import { useState } from "react";
import { getPublicToken } from "services/api/tokenPublico";
import { getIdSistema } from "services/api/idSistema";
import {
  LOCAL_STORE_ID_USUARIO,
  LOCAL_STORE_ITOKEN,
  LOCAL_STORE_TOKEN_BEARER,
  TOKEN_ADAGA_HML,
  TOKEN_ADAGA_PROD,
} from "../constants";
import { cifrarAES } from "../utils/cryptoAES";
import { showMessage } from "../utils/messages";

const MENSAGEM_HORARIO_RESTRITO_PADRAO =
  "Fora do horário de atendimento. Tente novamente durante o horário de funcionamento.";

export enum ApiMethod {
  GET = "GET",
  POST = "POST",
  PUT = "PUT",
  DELETE = "DELETE",
  PATCH = "PATCH",
}

export interface IResponseUseApi<TData = any> {
  status: number;
  loading?: boolean;
  error?: string;
  data?: TData;
  success?: boolean;
  response?: {} | any;
  mensagem?: null | any;
  // true quando o proprio hook ja exibiu o modal de horario restrito (401/403) —
  // quem chamou NAO deve mostrar um segundo modal de erro para essa mesma resposta.
  horarioRestritoTratado?: boolean;
}

export interface UseApiOptions {
  // Usa o token público (ms-usuario /auth/token-publico) no Authorization,
  // no lugar do fluxo de sessão logada. Só para endpoints noauth novos que
  // exigem esse handshake; sem a flag, comportamento é o original.
  usePublicToken?: boolean;
}

function useApi<T>(
  url: string,
  method: ApiMethod,
  body = {},
  headers = {},
  apiOptions: UseApiOptions = {},
) {
  const { usePublicToken = false } = apiOptions;
  const [responseApi, setResponseApi] = useState<IResponseUseApi>({
    status: 0,
    loading: false,
    error: "",
    data: null,
    success: false,
    response: {},
    mensagem: null,
  });

  const environment = process.env.REACT_APP_ENVIRONMENT;
  const callApi = (reqBody = {}) => FetchData(reqBody);

  const getOptions = async (reqBody = {}, url = "") => {
    const splitURL = url?.split("/");
    const reqHeaders: Headers = new Headers();
    const token = localStorage.getItem(LOCAL_STORE_ITOKEN);
    const token_Bearer = localStorage.getItem(LOCAL_STORE_TOKEN_BEARER);
    const xCode = sessionStorage.getItem("@NovaEra:xCode");
    const idUsuario = localStorage.getItem(LOCAL_STORE_ID_USUARIO);

    reqHeaders.set("Content-Type", "application/json");
    if (usePublicToken) {
      const publicToken = await getPublicToken();
      reqHeaders.set("Authorization", `Bearer ${publicToken}`);
      reqHeaders.set("Id-Sistema", getIdSistema());
    } else if (
      splitURL[3] !== "start-transaction" &&
      splitURL[3] !== "status"
    ) {
      if (token) reqHeaders.set("apikey", `token ${token.replaceAll('"', "")}`);
      if (token_Bearer)
        reqHeaders.set(
          "Authorization",
          `Bearer ${token_Bearer.replaceAll('"', "")}`,
        );
      if (xCode) reqHeaders.set("XCODE", xCode);
      if (idUsuario)
        reqHeaders.set("X-Id-Usuario-Criptografado", cifrarAES(idUsuario));
      reqHeaders.set("Id-Sistema", getIdSistema());
    } else {
      reqHeaders.set(
        "Authorization",
        environment === "PRODUCTION" ? TOKEN_ADAGA_PROD : TOKEN_ADAGA_HML,
      );
    }

    if (headers) Object.assign(reqHeaders, headers);

    let newBody = reqBody || body;

    if (!Array.isArray(body) && !Array.isArray(reqBody)) {
      newBody = Object.assign(body, reqBody);
    }

    let options: RequestInit = {
      method,
      headers: reqHeaders,
    };

    if (method !== ApiMethod.GET) {
      options = {
        ...options,
        body: JSON.stringify(newBody) || null,
      };
    }

    return options;
  };

  const FetchData = async (
    reqBody = {},
  ): Promise<{
    status: number;
    data?: object & T;
    response?: Response;
    mensagem?: string;
    horarioRestritoTratado?: boolean;
  }> => {
    setResponseApi({ ...responseApi, loading: true });

    const options = await getOptions(reqBody, url);
    let copyResponse;
    let json = null;

    try {
      const res = await fetch(url, options);
      copyResponse = res.clone();
      const statusResponse = res.status;

      if (res.status === 401 || res.status === 403) {
        // Fora do horario funcional (unico motivo de bloqueio para o totem — ver
        // HorarioFuncionalFilter no ms-usuario). Mostra a mensagem do backend aqui mesmo
        // e permanece na tela atual — sem navegar/recarregar.
        let mensagem: string | undefined;
        try {
          const errorBody = await res.clone().json();
          mensagem = errorBody?.mensagem;
        } catch (e) {
          // corpo do erro pode nao ser JSON
        }
        showMessage(
          "warning",
          "Atenção!",
          mensagem || MENSAGEM_HORARIO_RESTRITO_PADRAO,
          { timer: 5000, showConfirmButton: true },
        );
        const respostaHorarioRestrito = {
          loading: false,
          data: undefined,
          error: "",
          status: statusResponse,
          success: false,
          response: copyResponse,
          mensagem,
          horarioRestritoTratado: true,
        };
        setResponseApi(respostaHorarioRestrito);
        return respostaHorarioRestrito;
      }

      const contentType = res.headers.get("content-type");
      if (contentType && contentType.indexOf("application/json") !== -1) {
        json = await res.json();
      }

      setResponseApi({
        loading: false,
        data: json,
        error: "",
        status: statusResponse,
        success: true,
        response: res,
        mensagem: json ? json.mensagem : "",
      });
    } catch (err) {
      setResponseApi({
        loading: false,
        data: null,
        error: err
          ? String(err)
          : "Não foi possível se comunicar com o servidor",
        status: copyResponse ? copyResponse.status : 0,
        success: false,
        response: {},
        mensagem: null,
      });
    }

    return {
      status: copyResponse ? copyResponse.status : 0,
      data: json,
      response: copyResponse,
      mensagem: json ? json.mensagem : "",
    } as IResponseUseApi;
  };

  return { callApi, ...responseApi };
}

export default useApi;
